// 用户相关API接口
import apiClient, { API_ENDPOINTS } from "@/config/api";
import { config, isDevelopment } from "@/config/env";

/**
 * 用户API类
 */
export class UserAPI {
  /**
   * 用户登录
   * @param {Object} loginData - 登录数据
   * @param {string} loginData.username - 用户名
   * @param {string} loginData.password - 密码
   * @returns {Promise} API响应
   */
  static async login(loginData) {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.USER.LOGIN,
        loginData
      );
      return {
        success: true,
        data: response,
        message: "登录成功",
      };
    } catch (error) {
      console.error("登录失败:", error);

      // 如果是API业务错误(code=0)，显示具体错误消息
      if (error.apiResponse) {
        return {
          success: false,
          data: null,
          message: error.apiResponse.msg || "登录失败",
        };
      }

      // 如果是网络或其他错误，不重复显示(拦截器已处理)
      return {
        success: false,
        data: null,
        message: error.message || "登录失败",
      };
    }
  }

  /**
   * 用户注册
   * @param {Object} registerData - 注册数据
   * @returns {Promise} API响应
   */
  static async register(registerData) {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.USER.REGISTER,
        registerData
      );
      return {
        success: true,
        data: response.data || response,
        message: "注册成功",
      };
    } catch (error) {
      console.error("注册失败:", error);
      return {
        success: false,
        data: null,
        message: error.message || "注册失败",
      };
    }
  }

  /**
   * 用户退出登录
   * @returns {Promise} API响应
   */
  static async logout() {
    try {
      const response = await apiClient.post(API_ENDPOINTS.USER.LOGOUT);
      return {
        success: true,
        data: response.data || response,
        message: "退出成功",
      };
    } catch (error) {
      console.error("退出失败:", error);
      return {
        success: false,
        data: null,
        message: error.message || "退出失败",
      };
    }
  }

  /**
   * 获取用户信息
   * @returns {Promise} API响应
   */
  static async getProfile() {
    try {
      const response = await apiClient.get(API_ENDPOINTS.USER.PROFILE);
      return {
        success: true,
        data: response.data || response,
        message: "获取用户信息成功",
      };
    } catch (error) {
      console.error("获取用户信息失败:", error);
      return {
        success: false,
        data: null,
        message: error.message || "获取用户信息失败",
      };
    }
  }

  /**
   * 更新用户信息
   * @param {Object} userData - 用户数据
   * @returns {Promise} API响应
   */
  static async updateProfile(userData) {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.USER.UPDATE_PROFILE,
        userData
      );
      return {
        success: true,
        data: response.data || response,
        message: "更新用户信息成功",
      };
    } catch (error) {
      console.error("更新用户信息失败:", error);
      return {
        success: false,
        data: null,
        message: error.message || "更新用户信息失败",
      };
    }
  }

  /**
   * 重置密码
   * @param {Object} resetData - 重置密码数据
   * @returns {Promise} API响应
   */
  static async resetPassword(resetData) {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.USER.RESET_PASSWORD,
        resetData
      );
      return {
        success: true,
        data: response.data || response,
        message: "密码重置成功",
      };
    } catch (error) {
      console.error("密码重置失败:", error);
      return {
        success: false,
        data: null,
        message: error.message || "密码重置失败",
      };
    }
  }
}

/**
 * 模拟用户API（开发测试用）
 */
export class MockUserAPI {
  /**
   * 模拟用户登录
   */
  static async login({ username, password }) {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    // 模拟登录验证
    if (username === "demo" && password === "123456") {
      const userInfo = {
        id: 1,
        username: username,
        nickname: "演示用户",
        avatar: "",
        level: 5,
        totalWords: 1200,
        masteredWords: 980,
        email: "demo@example.com",
        registerDate: "2024-01-01",
        coins: 2580,
        continuousStudyDays: 15,
      };

      const token = "demo-token-" + Date.now();

      return {
        success: true,
        data: {
          user: userInfo,
          token: token,
        },
        message: "登录成功",
      };
    } else {
      return {
        success: false,
        data: null,
        message: "用户名或密码错误",
      };
    }
  }

  /**
   * 模拟用户注册
   */
  static async register(registerData) {
    await new Promise((resolve) => setTimeout(resolve, 1200));

    // 模拟注册验证
    if (registerData.username && registerData.password) {
      const userInfo = {
        id: Math.floor(Math.random() * 10000),
        username: registerData.username,
        nickname: registerData.nickname || registerData.username,
        avatar: "",
        level: 1,
        totalWords: 0,
        masteredWords: 0,
        email: registerData.email,
        registerDate: new Date().toISOString().split("T")[0],
        coins: 100,
        continuousStudyDays: 0,
      };

      const token = "new-user-token-" + Date.now();

      return {
        success: true,
        data: {
          user: userInfo,
          token: token,
        },
        message: "注册成功",
      };
    } else {
      return {
        success: false,
        data: null,
        message: "注册信息不完整",
      };
    }
  }

  /**
   * 模拟退出登录
   */
  static async logout() {
    await new Promise((resolve) => setTimeout(resolve, 300));

    return {
      success: true,
      data: null,
      message: "退出成功",
    };
  }

  /**
   * 模拟获取用户信息
   */
  static async getProfile() {
    await new Promise((resolve) => setTimeout(resolve, 500));

    const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");

    return {
      success: true,
      data: userInfo,
      message: "获取用户信息成功",
    };
  }

  /**
   * 模拟更新用户信息
   */
  static async updateProfile(userData) {
    await new Promise((resolve) => setTimeout(resolve, 800));

    return {
      success: true,
      data: userData,
      message: "更新用户信息成功",
    };
  }

  /**
   * 模拟重置密码
   */
  static async resetPassword() {
    await new Promise((resolve) => setTimeout(resolve, 1000));

    return {
      success: true,
      data: null,
      message: "密码重置链接已发送到您的邮箱",
    };
  }
}

// 根据环境选择使用真实API还是模拟API
const useMock = isDevelopment && config.USE_MOCK;

if (config.DEBUG) {
  console.log("🌍 API 环境配置:", {
    NODE_ENV: config.NODE_ENV,
    USE_MOCK: config.USE_MOCK,
    API_BASE_URL: config.API_BASE_URL,
    实际使用: useMock ? "模拟API" : "真实API",
  });
}

export default useMock ? MockUserAPI : UserAPI;
